import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { clearAuthStorage, notifyAuthUpdated } from "../utils/authSession";
import { getMyEntitlements, getPaymentsConfig } from "../utils/paymentsApi";
import { normalizeActiveProductIds, toNumericProductId } from "../utils/entitlements";

const AuthContext = createContext();
const DEFAULT_PAYMENTS_CONFIG = {
  payments_enforced: true,
  default_guest_limit: 99,
  products: [],
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [authPayload, setAuthPayload] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [activeProductIds, setActiveProductIds] = useState([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [paymentsConfig, setPaymentsConfig] = useState(DEFAULT_PAYMENTS_CONFIG);
  const [paymentsConfigLoading, setPaymentsConfigLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const syncFromStorage = () => {
      const raw = localStorage.getItem("authPayload");
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          setAuthPayload(parsed);
        } catch (e) {
          console.error("Invalid authPayload in localStorage:", e);
          localStorage.removeItem("authPayload");
          setAuthPayload(null);
        }
      } else {
        setAuthPayload(null);
      }

      const storedWedding = localStorage.getItem("wedding");
      if (storedWedding) {
        try {
          setWedding(JSON.parse(storedWedding));
        } catch (e) {
          console.error("Invalid wedding in localStorage:", e);
          localStorage.removeItem("wedding");
          setWedding(null);
        }
      } else {
        setWedding(null);
      }
    };

    syncFromStorage();

    const handleAuthUpdated = () => syncFromStorage();
    const handleAuthLoggedOut = () => {
      setAuthPayload(null);
      setWedding(null);
    };
    const handleStorage = (e) => {
      if (e.key === "authPayload" || e.key === "wedding") {
        syncFromStorage();
      }
    };

    window.addEventListener("auth-updated", handleAuthUpdated);
    window.addEventListener("auth-logged-out", handleAuthLoggedOut);
    window.addEventListener("storage", handleStorage);

    setLoading(false);

    return () => {
      window.removeEventListener("auth-updated", handleAuthUpdated);
      window.removeEventListener("auth-logged-out", handleAuthLoggedOut);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const fetchWedding = async () => {
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_BASE}/weddings/my`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error("Failed to fetch wedding");

      const data = await response.json();
      setWedding(data);
      localStorage.setItem("wedding", JSON.stringify(data));
      return data;
    } catch (error) {
      console.error("Error fetching wedding:", error);
      return null;
    }
  };

  const login = async ({
    id_token,
    refresh_token,
    user_email,
    firebase_uid,
    role,
  }) => {
    const payload = { id_token, refresh_token, user_email, firebase_uid, role };
    setAuthPayload(payload);
    localStorage.setItem("authPayload", JSON.stringify(payload));
    notifyAuthUpdated();

    await fetchWedding();
  };

  const signup = ({ user_email, firebase_uid }) => {
    const payload = { user_email, firebase_uid };
    setAuthPayload(payload);
    localStorage.setItem("authPayload", JSON.stringify(payload));
  };

  const logout = () => {
    setAuthPayload(null);
    setWedding(null);
    setEntitlements([]);
    setActiveProductIds([]);
    clearAuthStorage();
  };

  const refreshPaymentsConfig = useCallback(async () => {
    try {
      setPaymentsConfigLoading(true);
      const payload = await getPaymentsConfig();
      const nextConfig = {
        payments_enforced:
          payload?.payments_enforced === undefined
            ? true
            : Boolean(payload.payments_enforced),
        default_guest_limit:
          Number.isFinite(Number(payload?.default_guest_limit))
            ? Number(payload.default_guest_limit)
            : DEFAULT_PAYMENTS_CONFIG.default_guest_limit,
        products: Array.isArray(payload?.products) ? payload.products : [],
      };
      setPaymentsConfig(nextConfig);
      return nextConfig;
    } catch (error) {
      console.error("Error fetching payments config:", error);
      setPaymentsConfig(DEFAULT_PAYMENTS_CONFIG);
      return DEFAULT_PAYMENTS_CONFIG;
    } finally {
      setPaymentsConfigLoading(false);
    }
  }, []);

  const refreshEntitlements = useCallback(async () => {
    if (!paymentsConfig?.payments_enforced) {
      setEntitlements([]);
      setActiveProductIds([]);
      setEntitlementsLoading(false);
      return null;
    }

    if (!authPayload?.id_token) {
      setEntitlements([]);
      setActiveProductIds([]);
      setEntitlementsLoading(false);
      return null;
    }

    try {
      setEntitlementsLoading(true);
      const payload = await getMyEntitlements();
      setEntitlements(Array.isArray(payload?.entitlements) ? payload.entitlements : []);
      setActiveProductIds(normalizeActiveProductIds(payload));
      return payload;
    } catch (error) {
      console.error("Error fetching entitlements:", error);
      setEntitlements([]);
      setActiveProductIds([]);
      return null;
    } finally {
      setEntitlementsLoading(false);
    }
  }, [authPayload?.id_token, paymentsConfig?.payments_enforced]);

  const hasProduct = useCallback((productId) => {
    if (paymentsConfigLoading) {
      return true;
    }
    if (!paymentsConfig?.payments_enforced) {
      return true;
    }
    const normalizedProductId = toNumericProductId(productId);
    if (normalizedProductId === null) {
      return false;
    }
    return activeProductIds.includes(normalizedProductId);
  }, [activeProductIds, paymentsConfig?.payments_enforced, paymentsConfigLoading]);

  useEffect(() => {
    refreshPaymentsConfig();
  }, [refreshPaymentsConfig]);

  useEffect(() => {
    if (paymentsConfigLoading) {
      return;
    }
    if (!paymentsConfig?.payments_enforced) {
      setEntitlements([]);
      setActiveProductIds([]);
      setEntitlementsLoading(false);
      return;
    }
    if (!authPayload?.id_token) {
      setEntitlements([]);
      setActiveProductIds([]);
      setEntitlementsLoading(false);
      return;
    }

    refreshEntitlements();
  }, [
    authPayload?.id_token,
    paymentsConfig?.payments_enforced,
    paymentsConfigLoading,
    refreshEntitlements,
  ]);

  const value = {
    authPayload,
    wedding,
    weddingId: wedding?.id ?? null,
    entitlements,
    activeProductIds,
    entitlementsLoading,
    paymentsConfig,
    paymentsEnforced: Boolean(paymentsConfig?.payments_enforced),
    paymentsConfigLoading,
    hasProduct,
    refreshPaymentsConfig,
    refreshEntitlements,
    login,
    signup,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
